import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  Skeleton,
  StackDivider,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiHeart, FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import { getAuth } from 'firebase/auth';

import { buildKeepMoreCustomers } from '../utils/keepMoreCustomersBuilder';
import { logger } from '../utils/logger';
import { getRetentionPlays } from '../services/aiKeywordService';

const MotionBox = motion(Box);

const CACHE_PREFIX = 'localytics.keepMoreCustomers.v1.';

const getStorage = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage || null;
  } catch (_err) {
    return null;
  }
};

const readCache = (cacheKey) => {
  if (!cacheKey) return null;
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(`${CACHE_PREFIX}${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (Array.isArray(parsed.keep) || Array.isArray(parsed.reverse))) {
      return parsed;
    }
    return null;
  } catch (_err) {
    return null;
  }
};

const writeCache = (cacheKey, payload) => {
  if (!cacheKey) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(`${CACHE_PREFIX}${cacheKey}`, JSON.stringify(payload));
  } catch (_err) {
    /* localStorage may be full or disabled; safe to ignore. */
  }
};

const cleanThemes = (themes) =>
  (Array.isArray(themes) ? themes : [])
    .filter((t) => typeof t === 'string' && t.trim().length > 0)
    .map((t) => t.trim())
    .slice(0, 5);

const normalizeItems = (items) =>
  (Array.isArray(items) ? items : [])
    .map((it) => ({
      action: (it?.action || '').toString().trim(),
      detail: (it?.detail || '').toString().trim(),
    }))
    .filter((it) => it.action || it.detail)
    .slice(0, 2);

/**
 * CategoryBadge — compact pill combining a consistent-stroke icon with the
 * category label (Keep doing / Reverse …). Restrained accent tint instead of
 * a full colored outline.
 */
const CategoryBadge = ({ icon, label, color }) => (
  <HStack
    as="span"
    display="inline-flex"
    align="center"
    spacing={1}
    h="20px"
    px="8px"
    borderRadius="full"
    bg={`${color}.50`}
    color={`${color}.700`}
    border="1px solid"
    borderColor={`${color}.100`}
    flexShrink={0}
  >
    <Flex
      as="span"
      align="center"
      justify="center"
      w="11px"
      h="11px"
      flex="0 0 11px"
    >
      <Icon as={icon} boxSize="11px" strokeWidth={2.25} display="block" />
    </Flex>
    <Text
      as="span"
      display="block"
      fontSize="10px"
      fontWeight="700"
      textTransform="uppercase"
      letterSpacing="0.06em"
      lineHeight="1"
      m={0}
    >
      {label}
    </Text>
  </HStack>
);

const PlayGroup = ({
  title,
  icon,
  color,
  items,
  subtleText,
  headingColor,
  rowBg,
  rowBorder,
  rowHoverBg,
}) => {
  if (!items || items.length === 0) return null;

  return (
    <Box>
      <Box mb={1.5}>
        <CategoryBadge icon={icon} label={title} color={color} />
      </Box>

      <VStack align="stretch" spacing={1.5}>
        {items.map((item, idx) => (
          <Box
            key={idx}
            p={2.5}
            borderRadius="12px"
            bg={rowBg}
            border="1px solid"
            borderColor={rowBorder}
            transition="background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease"
            _hover={{
              bg: rowHoverBg,
              borderColor: `${color}.200`,
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
            }}
          >
            {item.action && (
              <Text
                fontSize="13px"
                fontWeight="700"
                color={headingColor}
                lineHeight="1.35"
                letterSpacing="-0.005em"
              >
                {item.action}
              </Text>
            )}
            {item.detail && (
              <Text
                fontSize="12px"
                color={subtleText}
                lineHeight="1.5"
                mt={item.action ? 1 : 0}
              >
                {item.detail}
              </Text>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

/**
 * KeepMoreCustomersCard
 *
 * The right-hand ("Keep More Customers") panel of the acquisition/retention
 * row below the Localytics Score breakdown. Renders internal retention
 * recommendations derived from a lightweight SWOT of the business's own
 * reviews, in two groups:
 *   - KEEP DOING — what their 5-star reviewers praise (protect it)
 *   - REVERSE    — what their negative reviewers complain about (fix it)
 *
 * Content is generated by Gemini (`/ai/generate-retention-plays`) for
 * authenticated users, with a deterministic theme-based fallback for
 * public landing/demo pages and AI failures. Results are cached in
 * localStorage per business so re-opening the modal never re-spends
 * Gemini credits.
 */
const KeepMoreCustomersCard = ({
  businessName,
  mapsUri,
  positiveThemes,
  negativeThemes,
  dataTestId,
}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestKeyRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const dividerColor = useColorModeValue('gray.100', 'gray.700');
  const rowBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rowBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('white', 'whiteAlpha.100');
  const tileBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const tileBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const tileIcon = useColorModeValue('gray.700', 'gray.200');
  const cardShadow = useColorModeValue(
    '0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)',
    '0 1px 3px rgba(0, 0, 0, 0.4)'
  );

  const cleanedPositives = cleanThemes(positiveThemes);
  const cleanedNegatives = cleanThemes(negativeThemes);

  const requestKey = JSON.stringify({
    businessName,
    mapsUri,
    positiveThemes: cleanedPositives,
    negativeThemes: cleanedNegatives,
  });

  useEffect(() => {
    if (requestKeyRef.current === requestKey) return;
    requestKeyRef.current = requestKey;

    const cacheKey = `${mapsUri || businessName}|${requestKey}`;
    const cached = readCache(cacheKey);
    if (cached) {
      setData({
        keep: normalizeItems(cached.keep),
        reverse: normalizeItems(cached.reverse),
      });
      setIsLoading(false);
      return;
    }

    const runFallback = () => {
      const built = buildKeepMoreCustomers({
        positiveThemes: cleanedPositives,
        negativeThemes: cleanedNegatives,
      });
      const payload = {
        keep: normalizeItems(built.keep),
        reverse: normalizeItems(built.reverse),
      };
      setData(payload);
      writeCache(cacheKey, payload);
      setIsLoading(false);
    };

    let firebaseUser = null;
    try {
      firebaseUser = getAuth()?.currentUser || null;
    } catch (_e) {
      firebaseUser = null;
    }

    if (!firebaseUser) {
      runFallback();
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getRetentionPlays({
      businessName,
      mapsUri,
      positiveThemes: cleanedPositives,
      negativeThemes: cleanedNegatives,
    })
      .then((response) => {
        if (cancelled) return;
        const payload = {
          keep: normalizeItems(response?.data?.keep),
          reverse: normalizeItems(response?.data?.reverse),
        };
        if (payload.keep.length > 0 || payload.reverse.length > 0) {
          setData(payload);
          writeCache(cacheKey, payload);
          setIsLoading(false);
        } else {
          runFallback();
        }
      })
      .catch((err) => {
        if (cancelled) return;
        logger.warn(
          'KeepMoreCustomersCard AI failed, using deterministic fallback:',
          err?.message || err
        );
        runFallback();
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasContent =
    data && ((data.keep && data.keep.length > 0) || (data.reverse && data.reverse.length > 0));

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      bg={cardBg}
      borderRadius="16px"
      p={{ base: 4, md: 5 }}
      border="1px solid"
      borderColor={cardBorder}
      boxShadow={cardShadow}
      position="relative"
      h="100%"
      data-testid={dataTestId || 'keep-more-customers-card'}
    >
      <Flex align="center" gap={2.5} pb={3} mb={3} borderBottom="1px solid" borderColor={dividerColor}>
        <Flex
          w="32px"
          h="32px"
          align="center"
          justify="center"
          borderRadius="10px"
          bg={tileBg}
          color={tileIcon}
          border="1px solid"
          borderColor={tileBorder}
          flexShrink={0}
        >
          <Icon as={FiHeart} boxSize="16px" strokeWidth={2.25} />
        </Flex>
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="700"
          color={headingColor}
          letterSpacing="-0.01em"
          lineHeight="1.2"
        >
          Keep More Customers
        </Text>
      </Flex>

      {isLoading && !data && (
        <VStack align="stretch" spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              height="52px"
              borderRadius="12px"
              startColor={rowBg}
              endColor={rowBorder}
            />
          ))}
        </VStack>
      )}

      {hasContent && (
        <VStack align="stretch" spacing={3} divider={<StackDivider borderColor={dividerColor} />}>
          {[
            {
              key: 'keep',
              title: 'Keep doing',
              icon: FiCheckCircle,
              color: 'green',
              items: data.keep,
            },
            {
              key: 'reverse',
              title: 'Reverse from negative reviews',
              icon: FiRotateCcw,
              color: 'red',
              items: data.reverse,
            },
          ]
            .filter((group) => group.items && group.items.length > 0)
            .map((group) => (
              <PlayGroup
                key={group.key}
                title={group.title}
                icon={group.icon}
                color={group.color}
                items={group.items}
                subtleText={subtleText}
                headingColor={headingColor}
                rowBg={rowBg}
                rowBorder={rowBorder}
                rowHoverBg={rowHoverBg}
              />
            ))}
        </VStack>
      )}
    </MotionBox>
  );
};

export default KeepMoreCustomersCard;
